import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { ManagedUser } from '@/model/user';
import { MatCardModule } from '@angular/material/card';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { UserService } from '@/service/user.service';
import { GraphqlService } from '@/service/graphql.service';
import { Category } from '@/model/category';
import { LoadingIndicationComponent } from '../loading-indication/loading-indication.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-managed-user-management',
  imports: [
    MatCardModule,
    FormsModule,
    LoadingIndicationComponent,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatProgressBarModule,
  ],
  templateUrl: './managed-user-management.component.html',
  styleUrl: './managed-user-management.component.scss',
  standalone: true,
})
export class ManagedUserManagementComponent implements OnInit {
  @Input() user: ManagedUser | null = null;
  @Output() userChanged = new EventEmitter<ManagedUser | null>();
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('deleteDialog') deleteDialog!: ElementRef<HTMLDialogElement>;

  managedUserForm: FormGroup;
  categories: Category[] = [];
  isUploading = false;
  pendingProfilePicture: File | null = null;
  temporaryImagePreview: string | null = null;

  loadingState = {
    loading: false,
    saving: false,
    deleting: false,
  };

  constructor(
    private userService: UserService,
    private graphqlService: GraphqlService,
    private fb: FormBuilder,
  ) {
    this.managedUserForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', []],
      gender: ['', [Validators.required]],
      birthYear: [
        '',
        [
          Validators.required,
          Validators.min(1900),
          Validators.max(new Date().getFullYear()),
        ],
      ],
      zipCode: ['', [Validators.required]],
      profilePictureUrl: [''],
      profilePictureId: [''],
      categories: [[], [Validators.required]],
    });

    this.managedUserForm.valueChanges.subscribe(() => {
      Object.keys(this.managedUserForm.controls).forEach((key) => {
        const control = this.managedUserForm.get(key);
        if (control && control.invalid) {
          console.log(`${key} is invalid:`);
          if (control.errors) {
            Object.keys(control.errors).forEach((errorKey) => {
              console.log(
                `- ${errorKey}: ${JSON.stringify(control.errors?.[errorKey])}`,
              );
            });
          }
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadCategories();
    if (this.user) {
      this.initializeForm();
    }
  }

  loadCategories() {
    this.graphqlService.allCategories().subscribe((categories) => {
      this.categories = categories.filter((category) => !category.parentId);
    });
  }

  initializeForm() {
    if (this.user) {
      this.managedUserForm.patchValue({
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        gender: this.user.gender,
        birthYear: this.user.birthYear,
        zipCode: this.user.zipCode,
        profilePictureUrl: this.user.profilePicture?.url || '',
        profilePictureId: this.user.profilePicture?.id || '',
        categories: this.user.categories.map((cat) => cat.id),
      });
    }
  }

  getCategoryName(categoryId: string): string | undefined {
    return this.categories.find((c) => c.id === categoryId)?.name;
  }

  get categoriesControl() {
    return this.managedUserForm.get('categories');
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      if (this.isValidImageFile(file)) {
        if (this.user) {
          // If user exists, upload immediately
          this.uploadProfilePicture(file);
        } else {
          // Store file for later upload and show preview
          this.pendingProfilePicture = file;
          this.createImagePreview(file);
        }
      } else {
        console.error('Invalid file type. Please select an image file.');
      }
    }
  }

  private createImagePreview(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.temporaryImagePreview = e.target?.result as string;
      this.managedUserForm.patchValue({
        profilePictureUrl: this.temporaryImagePreview,
      });
    };
    reader.readAsDataURL(file);
  }

  onSubmit() {
    if (this.managedUserForm.valid) {
      this.loadingState.saving = true;
      const formData = this.managedUserForm.value;

      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        birthYear: parseInt(formData.birthYear),
        zipCode: formData.zipCode,
        categoryIds: formData.categories,
      };

      // Choose between create and update based on whether we have a user
      const action = this.user
        ? this.userService.updateManagedUser(this.user.id!, userData)
        : this.userService.createManagedUser(userData);

      action.subscribe({
        next: (response: ManagedUser) => {
          this.user = response; // Update the user reference with the created/updated user
          let eventEmitted = false;

          // If there's a pending profile picture, upload it now
          if (
            this.pendingProfilePicture &&
            this.isValidImageFile(this.pendingProfilePicture)
          ) {
            this.uploadProfilePicture(this.pendingProfilePicture);
            this.pendingProfilePicture = null; // Clear the pending file
            eventEmitted = true;
          }

          this.loadingState.saving = false;
          // Reset form after successful creation
          if (!this.user) {
            this.managedUserForm.reset();
            this.managedUserForm.patchValue({
              categories: [],
              profilePictureUrl: '',
              profilePictureId: '',
            });
            this.temporaryImagePreview = null;
          }

          // Emit change event
          if (!eventEmitted) {
            this.userChanged.emit(this.user);
          }
        },
        error: (error: unknown) => {
          this.loadingState.saving = false;
          console.error('Error saving managed user:', error);
        },
      });
    }
  }

  private isValidImageFile(file: File): boolean {
    const acceptedImageTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    return acceptedImageTypes.includes(file.type);
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  private uploadProfilePicture(file: File) {
    if (!this.user) {
      console.error('Cannot upload profile picture: No managed user available');
      return;
    }

    this.isUploading = true;
    this.userService
      .uploadManagedUserProfilePicture(this.user, file)
      .subscribe({
        next: (profilePicture) => {
          this.managedUserForm.patchValue({
            profilePictureUrl: profilePicture.url,
            profilePictureId: profilePicture.id,
          });
          this.isUploading = false;
          this.userChanged.emit(this.user);
        },
        error: (error) => {
          console.error('Error uploading profile picture:', error);
          this.isUploading = false;
        },
      });
  }

  deleteUser() {
    if (!this.user || !this.user.id) {
      console.error('Cannot delete user: No managed user ID available');
      return;
    }
    this.deleteDialog.nativeElement.showModal();
  }

  confirmDelete() {
    if (!this.user || !this.user.id) return;

    this.loadingState.deleting = true;
    this.userService.deleteManagedUser(this.user.id).subscribe({
      next: () => {
        this.loadingState.deleting = false;
        this.userChanged.emit(null);
        this.deleteDialog.nativeElement.close();
      },
      error: (error) => {
        this.loadingState.deleting = false;
        console.error('Error deleting managed user:', error);
        this.deleteDialog.nativeElement.close();
      },
    });
  }

  cancelDelete() {
    this.deleteDialog.nativeElement.close();
  }
}
